import {
  faAlarmClock, faCalendarDay, faEnvelope, faFileSignature, faInbox, faListCheck, faSnooze,
} from '@fortawesome/pro-regular-svg-icons';

import { countdown, timeAgo } from 'helpers/date_utils';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { NextAction, ReasonCode } from './types';

type Bucket = 'deadlines' | 'leads' | 'quiet' | 'replies' | 'tasks';

type Action = 'confirm' | 'done' | 'email' | 'reply';

interface Reason {
  action?: Action;
  badge: string;
  bucket: Bucket;
  headline: (title: string) => string;
  icon: IconDefinition;
  move?: string;
  preview: 'own' | 'why';
  time?: (occurredAt: string, now: Date) => string | null;
}

const REASONS: Record<ReasonCode, Reason> = {
  contact_silent: {
    badge: 'Gone Quiet',
    bucket: 'quiet',
    headline: name => `Check in with ${name}`,
    icon: faSnooze,
    move: 'check in',
    preview: 'why',
  },
  critical_date_due: {
    badge: 'Deal Milestone',
    bucket: 'deadlines',
    headline: name => name,
    icon: faCalendarDay,
    move: 'clear the upcoming deadline',
    preview: 'own',
    time: countdown,
  },
  deal_close_date_passed: {
    badge: 'Deal Milestone Overdue',
    bucket: 'deadlines',
    headline: name => `Close out ${name}`,
    icon: faCalendarDay,
    move: 'confirm the close',
    preview: 'why',
  },
  deal_closing_soon: {
    action: 'confirm',
    badge: 'Deal Milestone',
    bucket: 'deadlines',
    headline: name => `Confirm dates for ${name}`,
    icon: faCalendarDay,
    move: 'prepare for closing',
    preview: 'why',
  },
  email_awaiting_reply: {
    action: 'reply',
    badge: 'Email',
    bucket: 'replies',
    headline: name => `Reply to ${name}`,
    icon: faEnvelope,
    move: 'reply to their email',
    preview: 'own',
    time: timeAgo,
  },
  lead_ca_signed: {
    action: 'email',
    badge: 'Signed CA',
    bucket: 'leads',
    headline: name => `Reach out to ${name}`,
    icon: faFileSignature,
    move: 'follow up on their CA',
    preview: 'own',
    time: timeAgo,
  },
  lead_inquired: {
    action: 'email',
    badge: 'Listing Inquiry',
    bucket: 'leads',
    headline: name => `Reach out to ${name}`,
    icon: faInbox,
    move: 'follow up on their inquiry',
    preview: 'own',
    time: timeAgo,
  },
  task_due_today: {
    action: 'done',
    badge: 'Task Due',
    bucket: 'tasks',
    headline: title => title,
    icon: faListCheck,
    preview: 'own',
  },
  task_overdue: {
    action: 'done',
    badge: 'Task Overdue',
    bucket: 'tasks',
    headline: title => title,
    icon: faAlarmClock,
    preview: 'own',
    time: timeAgo,
  },
  task_overdue_backlog: {
    action: 'done',
    badge: 'Task Overdue',
    bucket: 'tasks',
    headline: title => title,
    icon: faAlarmClock,
    preview: 'own',
    time: timeAgo,
  },
};

const LINK_LABELS: Record<Exclude<Action, 'done'>, string> = {
  confirm: 'Confirm dates',
  email: 'Email',
  reply: 'Reply',
};

const PEOPLE = ['Backoffice::Contact', 'ProjectLead'];

const SUMMARY: { bucket: Bucket; one: string; many: string }[] = [
  { bucket: 'replies', one: 'reply owed', many: 'replies owed' },
  { bucket: 'leads', one: 'new lead', many: 'new leads' },
  { bucket: 'tasks', one: 'task due', many: 'tasks due' },
  { bucket: 'deadlines', one: 'deal deadline', many: 'deal deadlines' },
  { bucket: 'quiet', one: 'contact gone quiet', many: 'contacts gone quiet' },
];

export function reasonFor(code: string): Reason | undefined {
  return REASONS[code as ReasonCode];
}

export type PrimaryAction = { kind: 'done' } | { href: string; kind: 'link'; label: string };

export function primaryActionFor(card: NextAction): PrimaryAction {
  const action = reasonFor(card.reasonCode)?.action;

  if (action === 'done' && card.completePath) {
    return { kind: 'done' };
  }

  const label = action && action !== 'done' ? LINK_LABELS[action] : card.linkLabel;
  return { href: card.url, kind: 'link', label };
}

export interface CardLayout {
  association: { label: string; url: string } | null;
  preview: string;
  time: string | null;
}

const NO_DETAILS = 'No details to show';

export function layoutFor(card: NextAction, now: Date = new Date()): CardLayout {
  const reason = reasonFor(card.reasonCode);
  const preview = reason?.preview === 'own' ? card.preview : card.why.join(' · ');

  return {
    association: card.record && { label: card.record.name, url: card.record.url },
    preview: preview || NO_DETAILS,
    time: card.occurredAt ? reason?.time?.(card.occurredAt, now) ?? null : null,
  };
}

export function summarize(cards: NextAction[]) {
  const counts = new Map<Bucket, number>();
  cards.forEach((card) => {
    const bucket = reasonFor(card.reasonCode)?.bucket;
    if (bucket) {
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
    }
  });

  const parts = SUMMARY
    .filter(({ bucket }) => counts.has(bucket))
    .map(({ bucket, many, one }) => {
      const count = counts.get(bucket) ?? 0;
      return `${count} ${count === 1 ? one : many}`;
    });

  return parts.length > 0 ? parts.join(' · ') : 'Nothing is waiting on you.';
}

function about(card: NextAction) {
  return card.subjectType === 'Apex::Task'
    ? { name: card.record?.name ?? null, type: card.record?.type }
    : { name: card.title, type: card.subjectType };
}

export function moveFor(card: NextAction): { move: string; name: string | null } {
  const reason = reasonFor(card.reasonCode);
  const title = card.title || 'Unnamed';
  if (!reason) {
    return { move: title, name: null };
  }

  const { name, type } = about(card);
  return { move: reason.move ?? title, name: type && PEOPLE.includes(type) ? card.firstName || name : name };
}

export function moveHeadline(card: NextAction) {
  const { move, name } = moveFor(card);
  return name ? `${name} → ${move}` : move;
}

export function moveReason(card: NextAction) {
  return card.why
    .filter(Boolean)
    .slice(0, 2)
    .map(line => `${line[0].toUpperCase()}${line.slice(1)}`)
    .map(line => (/[.!?]$/.test(line) ? line : `${line}.`))
    .join(' ');
}

export function viewLabel(card: NextAction) {
  if (!reasonFor(card.reasonCode)) {
    return card.linkLabel;
  }

  const { type } = about(card);
  if (type && PEOPLE.includes(type)) {
    return `View ${moveFor(card).name ?? 'record'}`;
  }

  return type === 'Apex::Deal' ? 'View deal' : 'Open task';
}

export function briefName(card: NextAction) {
  return about(card).name || card.title || 'That one';
}

export function briefPrompt(card: NextAction) {
  const move = `${moveFor(card).move}${card.why[0] ? ` (${card.why[0]})` : ''}`;

  return `${briefName(card)} is the top priority on my list right now — the move is to ${move}. `
    + 'In a few short paragraphs, each its own line starting with a bolded lead-in: what do I need to know, then '
    + "offer to help me do exactly that right now. Don't suggest anything else, like opening a deal.";
}
